import { Component, inject } from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import {
  ImageMedia,
  VideoMedia,
} from "../../../../../../features/media/domain/models/media";
import { BrandService } from "../../../../../../shared/services/api/brand.service";
import { PublicationTemplateForm } from "../../../components/publication-template-form/publication-template-form";
import { GooglePerformanceMaxFields } from "../../../data/entities/publication-template-fields.entity";
import { PublicationTemplateType } from "../../../data/entities/publication-templates.entity";
import {
  getLinkTypeFromLink,
  LinkType,
} from "../../../domain/models/facebook-carousel";
import { GooglePerformanceMax } from "../../../domain/models/google-performance-max";

export interface GooglePerformanceMaxForm {
  images: FormControl<ImageMedia[] | null>;
  logo: FormControl<ImageMedia | null>;
  videos: FormControl<VideoMedia[] | null>;
  link: FormControl<string | null>;
  linkType: FormControl<LinkType>;
  headlines: FormArray<FormControl<string | null>>;
  longHeadlines: FormArray<FormControl<string | null>>;
  descriptions: FormArray<FormControl<string | null>>;
}

@Component({
  selector: "app-google-performance-max-form",
  templateUrl: "./google-performance-max-form.component.html",
  styleUrls: [
    "../../../components/publication-template-form/publication-template-form.scss",
    "./google-performance-max-form.component.scss",
  ],
})
export class GooglePerformanceMaxFormComponent extends PublicationTemplateForm<GooglePerformanceMaxForm> {
  public fields!: GooglePerformanceMaxFields;

  protected readonly minNumHeadlines = 3;
  protected readonly minNumLongHeadlines = 1;
  protected readonly minNumDescriptions = 1;

  protected readonly maxNumHeadlines = 15;
  protected readonly maxNumLongHeadlines = 5;
  protected readonly maxNumDescriptions = 5;

  protected readonly brandId = inject(BrandService).currentBrandId;

  constructor() {
    super(PublicationTemplateType.GooglePerformanceMax);
  }

  public createForm(): void {
    this.form = new FormGroup<GooglePerformanceMaxForm>({
      images: new FormControl<ImageMedia[]>([]),
      logo: new FormControl<ImageMedia | null>(null),
      videos: new FormControl<VideoMedia[]>([]),
      linkType: new FormControl(LinkType.CampaignUrl, {
        validators: [Validators.required],
        nonNullable: true,
      }),
      link: new FormControl(null),
      headlines: new FormArray<FormControl<string | null>>([
        new FormControl<string | null>(null),
        new FormControl<string | null>(null),
        new FormControl<string | null>(null),
      ]),
      longHeadlines: new FormArray<FormControl<string | null>>([
        new FormControl<string | null>(null),
      ]),
      descriptions: new FormArray<FormControl<string | null>>([
        new FormControl<string | null>(null),
      ]),
    });
  }

  public setFormValues(ad: GooglePerformanceMax): void {
    this.form.controls.images.patchValue(ad.images);
    this.form.controls.logo.patchValue(ad.logos?.[0] ?? null);
    this.form.controls.videos.patchValue(ad.videos);
    this.form.controls.linkType.patchValue(getLinkTypeFromLink(ad.link));
    this.form.controls.link.patchValue(
      this.form.controls.linkType.value === LinkType.CustomUrl ? ad.link : null,
    );
    this.form.controls.longHeadlines = new FormArray<
      FormControl<string | null>
    >(
      ad.longHeadlines.map(
        (headline) => new FormControl<string | null>(headline),
      ),
    );
    this.form.controls.descriptions = new FormArray<FormControl<string | null>>(
      ad.descriptions.map(
        (headline) => new FormControl<string | null>(headline),
      ),
    );
    this.form.controls.headlines = new FormArray<FormControl<string | null>>(
      ad.headlines.map((headline) => new FormControl<string | null>(headline)),
    );
  }

  protected addTextControl(
    control: FormArray<FormControl<string | null>>,
  ): void {
    control.push(new FormControl<string | null>(null));
  }

  protected onActionDeleteMedia(control: FormControl, index: number): void {
    control.value.splice(index, 1);
  }
}
